import { ConsentSource, IUpdateNotificationPreferencesDTO } from 'common_library';
import { TranslateService } from '@ngx-translate/core';
import { NotificationPreferencesService } from '../../services/entities/notification-preferences.service';
import { ToastService } from '../../services/utils/toast.service';

/**
 * Stato + logica condivisa del form preferenze notifiche, usata sia dal modale
 * di primo avvio (NotificationPreferencesPage) sia dal pannello master-detail
 * (NotificationPreferencesDetailComponent). UNICA fonte di verità per default,
 * mapping prefs→toggle e costruzione del DTO di consenso: evita che i due
 * entry-point divergano su scelte rilevanti GDPR.
 *
 * Base class non decorata: le dipendenze arrivano dai costruttori delle
 * sottoclassi via super() (nessuna DI Angular diretta qui).
 */
export abstract class NotificationPreferencesFormBase {
  // Default allineati al backend: service*/personal* true (legittimo interesse),
  // marketing* false (GDPR opt-in). Le notifiche sui PAGAMENTI non hanno toggle:
  // sempre attive per obbligo di legge.
  servicePushEnabled = true;
  serviceEmailEnabled = true;
  marketingPushEnabled = false;
  marketingEmailEnabled = false;
  personalPushEnabled = true;
  personalEmailEnabled = true;

  isLoading = true;
  isSaving = false;

  /** Ultimi valori CONFERMATI dal server: per il revert se un auto-save fallisce. */
  private confirmed = this.currentValues();

  private currentValues(): Record<string, boolean> {
    return {
      servicePushEnabled: this.servicePushEnabled,
      serviceEmailEnabled: this.serviceEmailEnabled,
      marketingPushEnabled: this.marketingPushEnabled,
      marketingEmailEnabled: this.marketingEmailEnabled,
      personalPushEnabled: this.personalPushEnabled,
      personalEmailEnabled: this.personalEmailEnabled,
    };
  }

  constructor(
    protected readonly notificationPreferencesService: NotificationPreferencesService,
    protected readonly toast: ToastService,
    protected readonly translate: TranslateService,
  ) {}

  /** Carica le preferenze correnti nei toggle (default se mai salvate). */
  protected async loadPreferences(): Promise<void> {
    try {
      const prefs = await this.notificationPreferencesService.getMine();
      if (prefs) {
        this.servicePushEnabled = !!prefs.servicePushEnabled;
        this.serviceEmailEnabled = !!prefs.serviceEmailEnabled;
        this.marketingPushEnabled = !!prefs.marketingPushEnabled;
        this.marketingEmailEnabled = !!prefs.marketingEmailEnabled;
        this.personalPushEnabled = prefs.personalPushEnabled !== false;
        this.personalEmailEnabled = prefs.personalEmailEnabled !== false;
      }
      this.confirmed = this.currentValues();
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Salva il consenso (source MOBILE_APP) e mostra il toast. Ritorna true se
   * andato a buon fine. Idempotente sul flag isSaving. L'enforcement (chi riceve
   * cosa) è tutto server-side: le preferenze scritte qui sono l'unica fonte di
   * verità letta dal notification-service ad ogni invio (niente topic FCM).
   */
  protected async persistPreferences(): Promise<boolean> {
    if (this.isSaving) {
      return false;
    }
    this.isSaving = true;
    try {
      const dto: IUpdateNotificationPreferencesDTO = {
        servicePushEnabled: this.servicePushEnabled,
        serviceEmailEnabled: this.serviceEmailEnabled,
        marketingPushEnabled: this.marketingPushEnabled,
        marketingEmailEnabled: this.marketingEmailEnabled,
        personalPushEnabled: this.personalPushEnabled,
        personalEmailEnabled: this.personalEmailEnabled,
        source: ConsentSource.MOBILE_APP,
      };
      await this.notificationPreferencesService.updateMine(dto);
      this.confirmed = this.currentValues();
      this.toast.presentBasicToast({
        message: this.translate.instant('NOTIF_PREFS.SAVED'),
        color: 'secondary',
        icon: 'notifications-outline',
      });
      return true;
    } catch (err) {
      this.toast.presentBasicToast({ message: this.translate.instant('NOTIF_PREFS.CANT_SAVE'), color: 'danger', icon: 'warning' });
      return false;
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * Auto-save al cambio di un toggle (pannello "gestione"): persiste subito e
   * mostra il toast di esito. No-op durante il caricamento iniziale (i toggle
   * montano coi valori già caricati, non deve partire un save spurio).
   */
  async onToggleChange(): Promise<void> {
    if (this.isLoading) return;
    const ok = await this.persistPreferences();
    // Save fallito → ripristina l'ultimo stato confermato: la UI non deve mai
    // mostrare un valore che il server non ha accettato.
    if (!ok) Object.assign(this, this.confirmed);
  }
}
